import { FC, useEffect, useRef, useState, useTransition } from 'react';
import { RouteComponentProps, useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import { TextField, Autocomplete, Button, CircularProgress, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import SwitchSetting from 'src/app/common/components/SwitchSetting';
import Form from 'src/app/common/components/Form';
import { ModulePermissionProps } from 'src/app/common/module/types';
import {
  fetchQRCodeActivityTypeById,
  fetchQRCodeActivityCategoryList,
  createQRCodeActivityType,
  updateQRCodeActivityType,
} from '../../../network/activityTrackingCrud';
import { CategoryOption, QRCodeActivityType, EStatus } from '../../../types/activity-tracking-types';
import { ACTIVITY_TRACKING_BASE_PATH, QR_CODE_ACTIVITY_TYPE_PATH } from '../../../constans';
import { QRCodeActivityTypeCategoryRef } from 'src/app/modules/ActivityTracking/types/activity-tracking-api-types';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';

type QRCodeActivityTypeDetailPageProps = RouteComponentProps &
  ModulePermissionProps & {
    mode: 'create' | 'edit' | 'view';
  };

const useStyles = makeStyles()(() => ({
  container: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    borderLeft: '4px solid #DE1127',
    paddingLeft: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3f4254',
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  formLabel: {
    width: '180px',
    minWidth: '180px',
    fontWeight: 500,
    fontSize: '14px',
    color: '#3f4254',
  },
  required: {
    color: '#DE1127',
  },
  formInput: {
    flex: 1,
  },
  errorText: {
    color: '#F018A6',
    fontSize: '12px',
    marginTop: '4px',
  },
  frameworkRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    marginTop: '-8px',
  },
  frameworkLabel: {
    width: '180px',
    minWidth: '180px',
    fontWeight: 400,
    fontSize: '14px',
    color: '#3f4254',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  radioGroupLabel: {
    width: 100,
    color: '#979797',
  },
}));

const QRCodeActivityTypeDetailPage: FC<QRCodeActivityTypeDetailPageProps> = ({ mode, enableCreate, enableUpdate }) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const Translation = (msgId: string) => intl.formatMessage({ id: msgId });
  const [isPending, startTransition] = useTransition();
  const abortControllerRef = useRef<AbortController | null>(null);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const publishRef = useRef(false);
  const outdateRef = useRef(false);
  const currentCategory = useRef<QRCodeActivityTypeCategoryRef | null>(null);

  const initialValues: Partial<QRCodeActivityType> = {
    categoryId: '',
    framework: 'Group',
    name: '',
    description: '',
    enableLocationName: false,
    enableNote: false,
    status: EStatus.InActive,
    sortOrder: '',
  };

  const backPath = `${ACTIVITY_TRACKING_BASE_PATH}${QR_CODE_ACTIVITY_TYPE_PATH}/list`;

  const onFinish = async (values: Partial<QRCodeActivityType>) => {
    setSaving(true);
    const publish = publishRef.current;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const payload = {
      ...values,
      status: publish ? EStatus.Active : EStatus.InActive,
      sortOrder:
        values.sortOrder !== undefined && values.sortOrder !== null && String(values.sortOrder).length > 0
          ? Number(values.sortOrder)
          : null,
    };
    try {
      const response = isCreate
        ? await createQRCodeActivityType(payload, undefined, abortControllerRef.current.signal)
        : isEdit && id
        ? await updateQRCodeActivityType(id, payload, undefined, abortControllerRef.current.signal)
        : Promise.resolve({ data: null, isSuccess: false, error: { message: 'ID_NOT_PROVIDED' } });

      if (!response.isSuccess) {
        const error = response.error;
        if (error?.code === 'SORT_ORDER_DUPLICATE_MESSAGE') {
          form.setFields([
            {
              name: 'sortOrder',
              errors: ['Display Order number are used. Please input another number.'],
            },
          ]);
          return;
        }

        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: Translation('activityTracking.notify.error'),
            },
          ]),
        );
        return;
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: isCreate
              ? `${Translation('activityTracking.notify.createSuccess')} - ${values.name}`
              : `${Translation('activityTracking.notify.updateSuccess')} - ${values.name}`,
          },
        ]),
      );
      history.push(backPath);
    } catch (error: unknown) {
      const { message, ...rest } = error as any;
      if (message === 'SORT_ORDER_DUPLICATE_MESSAGE') {
        form.setFields([
          {
            name: 'sortOrder',
            errors: ['Display Order number are used. Please input another number.'],
          },
        ]);
      } else {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: Translation('activityTracking.notify.error'),
            },
          ]),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isCreate
    ? Translation('activityTracking.qrCodeActivityType.createTitle')
    : isEdit
    ? Translation('activityTracking.qrCodeActivityType.editTitle')
    : Translation('activityTracking.qrCodeActivityType.viewTitle');

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const [categoryResult, activityTypeResult] = await Promise.all([
          fetchQRCodeActivityCategoryList({ page: 1, limit: 1000, status: EStatus.Active }, dispatch),
          (isEdit || isView) && id ? fetchQRCodeActivityTypeById(id, dispatch) : Promise.resolve(null),
        ]);
        const opts = categoryResult.items.map((c) => ({
          value: c._id ?? '',
          label: c.name ?? '',
          framework: c.framework ?? 'Group',
        }));
        setCategoryOptions(opts);

        currentCategory.current = activityTypeResult?.category ?? null;
        outdateRef.current = currentCategory.current
          ? !opts.some((o) => o.value === currentCategory.current?._id)
          : false;

        if (activityTypeResult) {
          form.setFieldsValue({
            categoryId: outdateRef.current ? undefined : activityTypeResult?.category?._id,
            framework: activityTypeResult?.category?.framework || 'Group',
            name: activityTypeResult?.name,
            description: activityTypeResult?.description || '',
            enableLocationName: activityTypeResult?.enableLocationName,
            enableNote: activityTypeResult?.enableNote,
            sortOrder: activityTypeResult?.sortOrder || '',
            status: activityTypeResult?.status,
          });
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [dispatch, form, id, isEdit, isView]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <span className={classes.title}>{pageTitle}</span>
        <Button variant="contained" color="inherit" size="small" onClick={() => history.push(backPath)}>
          {Translation('app.button.back')}
        </Button>
      </div>

      <Form form={form} initialValues={initialValues} onFinish={onFinish}>
        {/* Category Name */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>
            {Translation('activityTracking.qrCodeActivityType.categoryName')}
            <span className={classes.required}> *</span>
          </div>
          <div className={classes.formInput}>
            <Form.Item
              name="categoryId"
              required={false}
              rules={[
                {
                  required: true,
                  message: Translation('activityTracking.validation.mandatoryField'),
                },
              ]}
            >
              {(control) => (
                <Autocomplete
                  disabled={isView}
                  options={categoryOptions}
                  fullWidth
                  value={
                    categoryOptions.find((o) => o.value === control.value) ||
                    (isView && id && currentCategory.current
                      ? {
                          value: currentCategory.current._id,
                          label: currentCategory.current.name,
                          framework: currentCategory.current.framework,
                        }
                      : null)
                  }
                  onChange={(_, newVal) => {
                    control.onChange(newVal?.value || '');
                    form.setFieldsValue({ framework: newVal?.framework || 'Group' });
                  }}
                  getOptionKey={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={params.inputProps?.value || ''}
                      size="small"
                      variant="outlined"
                      placeholder={Translation('app.input.placeholder.please-select')}
                    />
                  )}
                />
              )}
            </Form.Item>
          </div>
        </div>

        {/* Framework - radio buttons below Category Name */}
        <div className={classes.frameworkRow}>
          <div className={classes.frameworkLabel} />
          <div className={classes.frameworkRow}>
            <div className={classes.radioGroupLabel}>
              {Translation('activityTracking.qrCodeActivityType.framework')}
            </div>
            <Form.Item noStyle name="framework">
              {(control) => (
                <RadioGroup row value={control.value || 'Group'}>
                  <FormControlLabel
                    value="Group"
                    control={<Radio color="secondary" size="small" disabled />}
                    label={Translation('activityTracking.qrCodeActivityType.framework.group')}
                  />
                  <FormControlLabel
                    value="Individual"
                    control={<Radio color="secondary" size="small" disabled />}
                    label={Translation('activityTracking.qrCodeActivityType.framework.individual')}
                  />
                  <FormControlLabel
                    value="1-1"
                    control={<Radio color="secondary" size="small" disabled />}
                    label="1-1"
                  />
                </RadioGroup>
              )}
            </Form.Item>
          </div>
        </div>

        {/* Type Name */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>
            {Translation('activityTracking.qrCodeActivityType.typeName')}
            <span className={classes.required}> *</span>
          </div>
          <div className={classes.formInput}>
            <Form.Item
              name="name"
              required={false}
              rules={[
                {
                  required: true,
                  message: Translation('activityTracking.validation.mandatoryField'),
                },
              ]}
            >
              <TextField
                fullWidth
                placeholder={Translation('app.input.placeholder.please-input')}
                inputProps={{ maxLength: 100 }}
                disabled={isView}
                variant="outlined"
                size="small"
              />
            </Form.Item>
          </div>
        </div>

        {/* Description */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>{Translation('activityTracking.qrCodeActivityType.description')}</div>
          <div className={classes.formInput}>
            <Form.Item name="description" required={false}>
              <TextField
                fullWidth
                placeholder={Translation('app.input.placeholder.please-input')}
                inputProps={{ maxLength: 250 }}
                disabled={isView}
                variant="outlined"
                size="small"
              />
            </Form.Item>
          </div>
        </div>

        {/* Require Location? — hidden when framework = Individual */}
        <Form.Item noStyle>
          {(_, __, formInstance) => {
            const framework = formInstance.getFieldValue('framework');

            if (framework === 'Individual') {
              return null;
            }

            return (
              <div className={classes.formRow}>
                <div className={classes.formLabel}>
                  {Translation('activityTracking.qrCodeActivityType.requireLocation')}
                </div>
                <div className={classes.formInput}>
                  <Form.Item name="enableLocationName" required={false}>
                    {(control) => (
                      <SwitchSetting
                        checked={!!control.value}
                        disabled={isView}
                        onChange={(e) => control.onChange(e.target.checked)}
                      />
                    )}
                  </Form.Item>
                </div>
              </div>
            );
          }}
        </Form.Item>

        {/* Note */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>{Translation('activityTracking.qrCodeActivityType.note')}</div>
          <div className={classes.formInput}>
            <Form.Item name="enableNote" required={false}>
              {(control) => (
                <SwitchSetting
                  checked={!!control.value}
                  disabled={isView}
                  onChange={(e) => control.onChange(e.target.checked)}
                />
              )}
            </Form.Item>
          </div>
        </div>

        {/* Display Order */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>Display Order</div>
          <div className={classes.formInput}>
            <Form.Item
              name="sortOrder"
              required={false}
              rules={[
                {
                  validator: (_, value) => {
                    console.log({ value });
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    return Number(value) >= 1 && Number(value) <= 1000
                      ? Promise.resolve()
                      : Promise.reject(new Error('Display Order must be between 1 and 1000'));
                  },
                },
              ]}
            >
              {({ value, onChange }) => (
                <TextField
                  fullWidth
                  placeholder={Translation('app.input.placeholder.please-input')}
                  disabled={isView}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
            </Form.Item>
          </div>
        </div>

        {/* Actions */}
        {!isView && (enableCreate || enableUpdate) && (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                startTransition(() => {
                  publishRef.current = false;
                  form.submit();
                });
              }}
              disabled={saving || isPending}
            >
              {saving || isPending ? <CircularProgress size={20} /> : Translation('app.button.save')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                startTransition(() => {
                  publishRef.current = true;
                  form.submit();
                });
              }}
              disabled={saving || isPending}
            >
              {saving ? <CircularProgress size={20} /> : Translation('activityTracking.action.saveAndPublish')}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default QRCodeActivityTypeDetailPage;
